import { z } from 'zod';
import { defineCallable } from './callable-contract';
import { EngagementTemplateSchema } from './engagement';
import { SenderIdentityStatusSchema } from './sender-identity';

// ── uploadEngagementTemplate ────────────────────────────────────────────────

export const UploadEngagementTemplatePayloadSchema = z.object({
  organizationId: z.string().min(1),
  name: z.string().min(1),
  practiceArea: z.string().nullish(),
  fileBase64: z.string().min(1),
  fileName: z.string().min(1),
});
export type UploadEngagementTemplatePayload = z.infer<typeof UploadEngagementTemplatePayloadSchema>;

export const UploadEngagementTemplateResultSchema = z.object({
  success: z.boolean(),
  templateId: z.string(),
  boldsignTemplateId: z.string(),
});
export type UploadEngagementTemplateResult = z.infer<typeof UploadEngagementTemplateResultSchema>;

export const uploadEngagementTemplateContract = defineCallable('uploadEngagementTemplate', {
  input: UploadEngagementTemplatePayloadSchema,
  output: UploadEngagementTemplateResultSchema,
});

// ── sendEngagementLetter ────────────────────────────────────────────────────

export const SendEngagementLetterPayloadSchema = z.object({
  inquiryId: z.string().min(1),
  organizationId: z.string().min(1),
  templateId: z.string().min(1),
  senderAttorneyId: z.string().min(1),
});
export type SendEngagementLetterPayload = z.infer<typeof SendEngagementLetterPayloadSchema>;

export const SendEngagementLetterResultSchema = z.object({
  success: z.boolean(),
  documentId: z.string(),
});
export type SendEngagementLetterResult = z.infer<typeof SendEngagementLetterResultSchema>;

export const sendEngagementLetterContract = defineCallable('sendEngagementLetter', {
  input: SendEngagementLetterPayloadSchema,
  output: SendEngagementLetterResultSchema,
});

// ── validateEngagementTemplate ──────────────────────────────────────────────

export const ValidateEngagementTemplatePayloadSchema = z.object({
  organizationId: z.string().min(1),
  templateId: z.string().min(1),
});
export type ValidateEngagementTemplatePayload = z.infer<
  typeof ValidateEngagementTemplatePayloadSchema
>;

export const ValidateEngagementTemplateResultSchema = z.discriminatedUnion('valid', [
  z.object({ valid: z.literal(true) }),
  z.object({ valid: z.literal(false), missingRoles: z.array(z.string()) }),
]);
export type ValidateEngagementTemplateResult = z.infer<
  typeof ValidateEngagementTemplateResultSchema
>;

export const validateEngagementTemplateContract = defineCallable('validateEngagementTemplate', {
  input: ValidateEngagementTemplatePayloadSchema,
  output: ValidateEngagementTemplateResultSchema,
});

// ── createSenderIdentity ────────────────────────────────────────────────────

export const CreateSenderIdentityPayloadSchema = z.object({
  organizationId: z.string().min(1),
  targetUserId: z.string().min(1).nullish(),
  name: z.string().min(1),
  email: z.email(),
});
export type CreateSenderIdentityPayload = z.infer<typeof CreateSenderIdentityPayloadSchema>;

export const CreateSenderIdentityResultSchema = z.object({
  success: z.boolean(),
  senderIdentityId: z.string(),
  status: z.enum(['pending', 'approved']),
});
export type CreateSenderIdentityResult = z.infer<typeof CreateSenderIdentityResultSchema>;

export const createSenderIdentityContract = defineCallable('createSenderIdentity', {
  input: CreateSenderIdentityPayloadSchema,
  output: CreateSenderIdentityResultSchema,
});

// ── deleteSenderIdentity ────────────────────────────────────────────────────

export const DeleteSenderIdentityPayloadSchema = z.object({
  organizationId: z.string().min(1),
  targetUserId: z.string().min(1).nullish(),
});
export type DeleteSenderIdentityPayload = z.infer<typeof DeleteSenderIdentityPayloadSchema>;

export const DeleteSenderIdentityResultSchema = z.object({ success: z.boolean() });
export type DeleteSenderIdentityResult = z.infer<typeof DeleteSenderIdentityResultSchema>;

export const deleteSenderIdentityContract = defineCallable('deleteSenderIdentity', {
  input: DeleteSenderIdentityPayloadSchema,
  output: DeleteSenderIdentityResultSchema,
});

// ── resendSenderIdentityApproval ────────────────────────────────────────────

export const ResendSenderIdentityApprovalPayloadSchema = z.object({
  organizationId: z.string().min(1),
  targetUserId: z.string().min(1).nullish(),
});
export type ResendSenderIdentityApprovalPayload = z.infer<
  typeof ResendSenderIdentityApprovalPayloadSchema
>;

export const ResendSenderIdentityApprovalResultSchema = z.object({ success: z.boolean() });
export type ResendSenderIdentityApprovalResult = z.infer<
  typeof ResendSenderIdentityApprovalResultSchema
>;

export const resendSenderIdentityApprovalContract = defineCallable('resendSenderIdentityApproval', {
  input: ResendSenderIdentityApprovalPayloadSchema,
  output: ResendSenderIdentityApprovalResultSchema,
});

// ── checkSenderIdentityStatus ───────────────────────────────────────────────

export const CheckSenderIdentityStatusPayloadSchema = z.object({
  organizationId: z.string().min(1),
  targetUserId: z.string().min(1).nullish(),
});
export type CheckSenderIdentityStatusPayload = z.infer<
  typeof CheckSenderIdentityStatusPayloadSchema
>;

export const CheckSenderIdentityStatusResultSchema = z.object({
  status: SenderIdentityStatusSchema,
  email: z.string(),
  name: z.string(),
});
export type CheckSenderIdentityStatusResult = z.infer<typeof CheckSenderIdentityStatusResultSchema>;

export const checkSenderIdentityStatusContract = defineCallable('checkSenderIdentityStatus', {
  input: CheckSenderIdentityStatusPayloadSchema,
  output: CheckSenderIdentityStatusResultSchema,
});

// ── deleteEngagementTemplate ────────────────────────────────────────────────

export const DeleteEngagementTemplatePayloadSchema = z.object({
  organizationId: z.string().min(1),
  templateId: z.string().min(1),
});
export type DeleteEngagementTemplatePayload = z.infer<typeof DeleteEngagementTemplatePayloadSchema>;

export const DeleteEngagementTemplateResultSchema = z.object({ success: z.boolean() });
export type DeleteEngagementTemplateResult = z.infer<typeof DeleteEngagementTemplateResultSchema>;

export const deleteEngagementTemplateContract = defineCallable('deleteEngagementTemplate', {
  input: DeleteEngagementTemplatePayloadSchema,
  output: DeleteEngagementTemplateResultSchema,
});

// ── listEngagementTemplates ─────────────────────────────────────────────────

export const ListEngagementTemplatesPayloadSchema = z.object({
  organizationId: z.string().min(1),
});
export type ListEngagementTemplatesPayload = z.infer<typeof ListEngagementTemplatesPayloadSchema>;

export const ListEngagementTemplatesResultSchema = z.array(EngagementTemplateSchema);
export type ListEngagementTemplatesResult = z.infer<typeof ListEngagementTemplatesResultSchema>;

export const listEngagementTemplatesContract = defineCallable('listEngagementTemplates', {
  input: ListEngagementTemplatesPayloadSchema,
  output: ListEngagementTemplatesResultSchema,
});

// ── getSignedDocumentUrl ────────────────────────────────────────────────────

export const GetSignedDocumentUrlPayloadSchema = z.object({
  inquiryId: z.string().min(1),
  organizationId: z.string().min(1),
});
export type GetSignedDocumentUrlPayload = z.infer<typeof GetSignedDocumentUrlPayloadSchema>;

export const GetSignedDocumentUrlResultSchema = z.object({ url: z.string() });
export type GetSignedDocumentUrlResult = z.infer<typeof GetSignedDocumentUrlResultSchema>;

export const getSignedDocumentUrlContract = defineCallable('getSignedDocumentUrl', {
  input: GetSignedDocumentUrlPayloadSchema,
  output: GetSignedDocumentUrlResultSchema,
});

// ── getSigningLink ──────────────────────────────────────────────────────────

export const GetSigningLinkPayloadSchema = z.object({
  inquiryId: z.string().min(1),
  organizationId: z.string().min(1),
  signerEmail: z.email(),
});
export type GetSigningLinkPayload = z.infer<typeof GetSigningLinkPayloadSchema>;

export const GetSigningLinkResultSchema = z.object({ signingLink: z.string() });
export type GetSigningLinkResult = z.infer<typeof GetSigningLinkResultSchema>;

export const getSigningLinkContract = defineCallable('getSigningLink', {
  input: GetSigningLinkPayloadSchema,
  output: GetSigningLinkResultSchema,
});

// ── getTemplateEditUrl ──────────────────────────────────────────────────────

export const GetTemplateEditUrlPayloadSchema = z.object({
  organizationId: z.string().min(1),
  templateId: z.string().min(1),
});
export type GetTemplateEditUrlPayload = z.infer<typeof GetTemplateEditUrlPayloadSchema>;

export const GetTemplateEditUrlResultSchema = z.object({
  editUrl: z.string(),
  expiresAt: z.number(),
  warning: z.string().nullish(),
});
export type GetTemplateEditUrlResult = z.infer<typeof GetTemplateEditUrlResultSchema>;

export const getTemplateEditUrlContract = defineCallable('getTemplateEditUrl', {
  input: GetTemplateEditUrlPayloadSchema,
  output: GetTemplateEditUrlResultSchema,
});

// ── revokeEngagementLetter ──────────────────────────────────────────────────

export const RevokeEngagementLetterPayloadSchema = z.object({
  inquiryId: z.string().min(1),
  organizationId: z.string().min(1),
});
export type RevokeEngagementLetterPayload = z.infer<typeof RevokeEngagementLetterPayloadSchema>;

export const RevokeEngagementLetterResultSchema = z.object({ success: z.boolean() });
export type RevokeEngagementLetterResult = z.infer<typeof RevokeEngagementLetterResultSchema>;

export const revokeEngagementLetterContract = defineCallable('revokeEngagementLetter', {
  input: RevokeEngagementLetterPayloadSchema,
  output: RevokeEngagementLetterResultSchema,
});

// ── saveEngagementDraft ─────────────────────────────────────────────────────

export const SaveEngagementDraftPayloadSchema = z.object({
  inquiryId: z.string().min(1),
  organizationId: z.string().min(1),
  templateId: z.string().min(1),
});
export type SaveEngagementDraftPayload = z.infer<typeof SaveEngagementDraftPayloadSchema>;

export const SaveEngagementDraftResultSchema = z.object({ success: z.boolean() });
export type SaveEngagementDraftResult = z.infer<typeof SaveEngagementDraftResultSchema>;

export const saveEngagementDraftContract = defineCallable('saveEngagementDraft', {
  input: SaveEngagementDraftPayloadSchema,
  output: SaveEngagementDraftResultSchema,
});
